/**
 * CSS Variables Updater
 * 
 * Updates CSS variables for live preview based on settings and components
 * 
 * @package TopBuddy
 */

/**
 * Update CSS variables on the preview element
 * 
 * @param {Object} settings - Bar settings
 * @param {Array} components - Active components
 * @param {Object} elementRef - React ref to the preview element
 */
export const updateCSSVariables = (settings, components, elementRef = null) => {
	const cssVars = {};

	// Generate CSS variables from bar settings
	if (settings) {
		if (settings.backgroundColor) {
			cssVars['--topbuddy-notification-bar-bg-color'] = settings.backgroundColor;
		}

		const backgroundType = settings.backgroundType || (settings.backgroundGradient ? 'gradient' : 'classic');
		const overlayOpacity = settings.backgroundOverlayOpacity !== undefined ? settings.backgroundOverlayOpacity : 0.6;

		if (backgroundType === 'gradient') {
			cssVars['--topbuddy-notification-bar-bg-gradient'] = settings.backgroundGradient || 'none';
			cssVars['--topbuddy-notification-bar-bg-image'] = 'none';
			// Don't cover gradients with the color overlay.
			cssVars['--topbuddy-notification-bar-overlay-opacity'] = 0;
		} else {
			cssVars['--topbuddy-notification-bar-bg-gradient'] = 'none';
			cssVars['--topbuddy-notification-bar-bg-image'] = settings.backgroundImage ? `url(${settings.backgroundImage})` : 'none';
			cssVars['--topbuddy-notification-bar-overlay-opacity'] = overlayOpacity;
		}
		
		if (settings.barWidth) {
			cssVars['--topbuddy-notification-bar-width'] = settings.barWidth;
		}

		if (settings.padding) {
			cssVars['--topbuddy-notification-bar-padding'] = `var(--topbuddy-notification-bar-padding-${settings.padding})`;
		}

		if (settings.alignment) {
			cssVars['--topbuddy-notification-bar-alignment'] = settings.alignment;
		}

		// Close button colors
		if (settings.closeColor) {
			cssVars['--topbuddy-notification-bar-close-color'] = settings.closeColor;
		}
		if (settings.closeHoverColor) {
			cssVars['--topbuddy-notification-bar-close-hover-color'] = settings.closeHoverColor;
		}
		if (settings.closeBgColor) {
			cssVars['--topbuddy-notification-bar-close-bg-color'] = settings.closeBgColor;
		}
		if (settings.closeHoverBgColor) {
			cssVars['--topbuddy-notification-bar-close-hover-bg-color'] = settings.closeHoverBgColor;
		}
	}

	// Generate CSS variables from components
	if (components && Array.isArray(components)) {
		const processedTypes = [];

		components.forEach(component => {
			if (!component.id || processedTypes.includes(component.id)) {
				return;
			}

			const vars = componentToCSSVars(component.id, component.settings || {});
			Object.assign(cssVars, vars);
			processedTypes.push(component.id);
		});
	}

	// Apply CSS variables to the preview element
	applyVariablesToPreview(cssVars, elementRef);
};

/**
 * Convert component settings to CSS variables
 * 
 * @param {string} componentId - Component ID
 * @param {Object} settings - Component settings
 * @returns {Object} CSS variables object
 */
const componentToCSSVars = (componentId, settings) => {
	const vars = {};

	switch (componentId) {
		case 'title':
			if (settings.color) {
				vars['--topbuddy-notification-bar-title-color'] = settings.color;
			}
			if (settings.size) {
				vars['--topbuddy-notification-bar-title-font-size'] = `var(--topbuddy-notification-bar-title-size-${settings.size})`;
			}
				if (settings.margin?.left) {
					vars['--topbuddy-notification-bar-title-margin-left'] = settings.margin.left;
				}
				if (settings.margin?.right) {
					vars['--topbuddy-notification-bar-title-margin-right'] = settings.margin.right;
				}
			break;

		case 'message':
			if (settings.color) {
				vars['--topbuddy-notification-bar-message-color'] = settings.color;
			}
			if (settings.size) {
				vars['--topbuddy-notification-bar-message-font-size'] = `var(--topbuddy-notification-bar-message-size-${settings.size})`;
			}
			if (settings.margin?.left) {
				vars['--topbuddy-notification-bar-message-margin-left'] = settings.margin.left;
			}
			if (settings.margin?.right) {
				vars['--topbuddy-notification-bar-message-margin-right'] = settings.margin.right;
			}
			break;

		case 'button':
			if (settings.color) {
				vars['--topbuddy-notification-bar-button-color'] = settings.color;
			}
			if (settings.bg_color) {
				vars['--topbuddy-notification-bar-button-bg-color'] = settings.bg_color;
			}
			if (settings.hover_color) {
				vars['--topbuddy-notification-bar-button-hover-color'] = settings.hover_color;
			}
			if (settings.hover_bg_color) {
				vars['--topbuddy-notification-bar-button-hover-bg-color'] = settings.hover_bg_color;
			}
			if (settings.size) {
				vars['--topbuddy-notification-bar-button-size'] = `var(--topbuddy-notification-bar-button-${settings.size})`;
			}
			break;

		case 'icon':
			if (settings.color) {
				vars['--topbuddy-notification-bar-icon-color'] = settings.color;
			}
			if (settings.color_hover) {
				vars['--topbuddy-notification-bar-icon-hover-color'] = settings.color_hover;
			}
			if (settings.size) {
				vars['--topbuddy-notification-bar-icon-font-size'] = `var(--topbuddy-notification-bar-icon-size-${settings.size})`;
			}
			break;

		case 'rotating_text':
			if (settings.color) {
				vars['--topbuddy-notification-bar-rotating-text-color'] = settings.color;
			}
			if (settings.size) {
				vars['--topbuddy-notification-bar-rotating-text-font-size'] = `var(--topbuddy-notification-bar-rotating-text-size-${settings.size})`;
			}
			break;

		case 'countdown':
			if (settings.value_color) {
				vars['--topbuddy-notification-bar-countdown-value-color'] = settings.value_color;
			}
			if (settings.label_color) {
				vars['--topbuddy-notification-bar-countdown-label-color'] = settings.label_color;
			}
			if (settings.item_background_color) {
				vars['--topbuddy-notification-bar-countdown-item-bg-color'] = settings.item_background_color;
			}
			if (settings.value_size) {
				vars['--topbuddy-notification-bar-countdown-value-font-size'] = `var(--topbuddy-notification-bar-countdown-size-${settings.value_size})`;
			}
			if (settings.label_size) {
				vars['--topbuddy-notification-bar-countdown-label-font-size'] = `var(--topbuddy-notification-bar-countdown-size-${settings.label_size})`;
			}
			break;
	}

	return vars;
};

/**
 * Apply CSS variables to the preview element
 * 
 * @param {Object} cssVars - CSS variables to apply
 * @param {Object} elementRef - React ref to the preview element
 */
const applyVariablesToPreview = (cssVars, elementRef = null) => {
	// Use ref if provided, otherwise fall back to querySelector for backward compatibility
	const previewElement = elementRef?.current || document.querySelector('.topbuddy-notification-bar');
	
	if (!previewElement) {
		return;
	}

	// Apply each CSS variable to the preview element
	Object.keys(cssVars).forEach(property => {
		previewElement.style.setProperty(property, cssVars[property]);
	});
};
